// The presentation queue for Throckmorton's lines: one beat on screen at a
// time, in the order they arrived, each held long enough to be read (or
// heard) before the next one replaces it. See docs/glasses-client's
// R-ONB-02 (first-contact lines in pieces), R-DISP-03 (one cue at a time,
// then clear), and R-DISP-04 (never another screen to stare at).
import type { SessionLine } from './ExperienceSession';
import type { UtteranceSource } from '../../ports/ExperiencePort';

/** The pause between one beat clearing and the next one showing. */
export const BEAT_PAUSE_MS = 600;

/** Meta's toast rule: 3.5s baseline, plus 300ms per word over two, capped at 8s. */
export const TOAST_MIN_MS = 3500;
export const TOAST_PER_WORD_MS = 300;
export const TOAST_MAX_MS = 8000;

/** Never present a single line as more than this many pieces. */
export const STACCATO_MAX_PIECES = 4;

/** A beat is either the live question the player still owes an answer to, or a passing line. */
export type BeatKind = 'active' | 'transient';

export interface Beat {
  /** Original line owns its cues and authored title throughout presentation. */
  line?: SessionLine;
  text: string;
  kind: BeatKind;
}

/** Meta's toast rule applied to one piece of text: 3.5s + 300ms per word over 2, max 8s. */
export function toastDurationMs(text: string): number {
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const extraWords = Math.max(0, wordCount - 2);
  return Math.min(TOAST_MAX_MS, TOAST_MIN_MS + extraWords * TOAST_PER_WORD_MS);
}

/** True for a line the staccato rule is allowed to touch: an opening/entry line. */
function isEntryLine(source: UtteranceSource | undefined): boolean {
  if (!source) return false;
  if (source.kind === 'generated_mission_entry') return true;
  return Boolean(source.lineId && source.lineId.includes('opening'));
}

/**
 * Splits an opening/entry line into short-sentence pieces for staccato
 * delivery (R-ONB-02): "Greetings, Agent. / I'm Throckmorton. / From the
 * Agency. / Before we go anywhere, how should I address you?" reads as four
 * separate arrivals rather than one paragraph.
 *
 * Only entry lines are eligible (source.kind "generated_mission_entry", or a
 * line_id containing "opening"); every other line is returned whole. A
 * single sentence is never split, even on an entry line. At most
 * STACCATO_MAX_PIECES pieces come back: when a line has more sentences than
 * that, the extra sentences join the last piece rather than being dropped.
 * When unsure whether a line qualifies, this returns it whole.
 */
export function splitStaccato(text: string, source?: UtteranceSource): string[] {
  const trimmed = text.trim();
  if (!trimmed || !isEntryLine(source)) return [text];

  const sentences = trimmed
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

  if (sentences.length <= 1) return [text];
  if (sentences.length <= STACCATO_MAX_PIECES) return sentences;

  const head = sentences.slice(0, STACCATO_MAX_PIECES - 1);
  const tail = sentences.slice(STACCATO_MAX_PIECES - 1).join(' ');
  return [...head, tail];
}

/** Whether a line carrying this source is the one the player still owes an answer to. */
/**
 * A line persists (active) unless the server says outright that the step is not
 * waiting. The server-held utterance contract treats a missing "response" as
 * "leave the current affordances unchanged", so a line without it must not
 * clear Throckmorton's question while he is still waiting for an answer.
 */
export function beatKindForSource(source: UtteranceSource | undefined): BeatKind {
  return source?.response?.awaiting === false ? 'transient' : 'active';
}

export interface BeatQueuePorts {
  setTimeout?: (run: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeout?: (handle: ReturnType<typeof setTimeout>) => void;
}

interface QueueItem extends Beat {
  text: string;
  kind: BeatKind;
  /** Milliseconds to hold this piece, or null to wait for an external end signal (speech playback). */
  holdMs: number | null;
}

/**
 * Presents one beat at a time from a FIFO queue. Each item is held for its
 * duration (or, when holdMs is null, until endHold() is called — the voice
 * playback case), then a BEAT_PAUSE_MS gap with nothing showing, then the
 * next item replaces it. An 'active' beat with nothing queued behind it
 * keeps showing indefinitely: nothing clears the player's live question but
 * a beat that replaces it. A 'transient' beat with nothing behind it clears
 * to nothing once its hold ends, so the card goes empty rather than stale.
 */
export class BeatQueue {
  private items: QueueItem[] = [];
  private currentItem: QueueItem | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** True once the current item's hold has ended and it is only waiting out the pause (or sitting settled). */
  private held = false;
  private readonly listeners = new Set<() => void>();
  /**
   * The same object reference until the beat actually changes, so a caller
   * using this as a React useSyncExternalStore snapshot never sees a "new"
   * value on a render that didn't follow a notify() (which would otherwise
   * loop forever).
   */
  private snapshot: Beat | null = null;

  constructor(private readonly ports: BeatQueuePorts = {}) {}

  get current(): Beat | null {
    return this.snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Adds one already-split piece to the end of the queue. */
  enqueue(text: string, kind: BeatKind, holdMs: number | null, line?: SessionLine): void {
    this.items.push({ text, kind, holdMs, ...(line ? { line } : {}) });
    if (this.currentItem === null) {
      this.startNext();
    } else if (this.held) {
      // The current item already finished its hold and nothing was queued
      // behind it (an active beat sitting settled, or a transient beat
      // already cleared): this new item still owes the pause before it can show.
      this.clearTimer();
      this.timer = this.schedule(() => {
        this.timer = null;
        this.startNext();
      }, BEAT_PAUSE_MS);
    }
  }

  /** Speech owns the visible line; discard older timed text when its next line starts. */
  showSpoken(text: string, kind: BeatKind, line?: SessionLine, holdMs: number | null = null): void {
    this.clearTimer();
    this.items = [];
    this.currentItem = { text, kind, holdMs, ...(line ? { line } : {}) };
    this.held = false;
    this.setSnapshot({ text, kind, ...(line ? { line } : {}) });
    if (holdMs !== null) this.timer = this.schedule(() => { this.timer = null; this.onHoldEnd(); }, holdMs);
  }

  /** Ends the current item's hold early: the voice-playback-finished signal. */
  endHold(): void {
    // These lines arrived during the same playback window. Once it ends,
    // queued lines cannot wait for that already-consumed signal again.
    for (const item of this.items) {
      if (item.holdMs === null) item.holdMs = toastDurationMs(item.text);
    }
    if (this.currentItem && this.currentItem.holdMs === null && !this.held) {
      this.onHoldEnd();
    }
  }

  dispose(): void {
    this.clearTimer();
    this.listeners.clear();
  }

  private startNext(): void {
    const next = this.items.shift();
    if (!next) {
      this.currentItem = null;
      this.held = false;
      this.setSnapshot(null);
      return;
    }
    this.currentItem = next;
    this.held = false;
    this.setSnapshot({ text: next.text, kind: next.kind, ...(next.line ? { line: next.line } : {}) });
    if (next.holdMs !== null) {
      this.timer = this.schedule(() => {
        this.timer = null;
        this.onHoldEnd();
      }, Math.max(0, next.holdMs));
    }
    // holdMs === null: wait for endHold() instead of a timer.
  }

  private onHoldEnd(): void {
    this.held = true;
    if (this.currentItem && this.currentItem.kind === 'transient') {
      this.currentItem = null;
      this.setSnapshot(null);
    }
    if (this.items.length > 0) {
      this.timer = this.schedule(() => {
        this.timer = null;
        this.startNext();
      }, BEAT_PAUSE_MS);
    }
    // Otherwise: sit settled. enqueue() picks the pause back up when the next item arrives.
  }

  private setSnapshot(beat: Beat | null): void {
    this.snapshot = beat;
    this.notify();
  }

  private notify(): void {
    for (const listener of [...this.listeners]) listener();
  }

  private schedule(run: () => void, ms: number): ReturnType<typeof setTimeout> {
    const schedule = this.ports.setTimeout ?? ((fn: () => void, delay: number) => setTimeout(fn, delay));
    return schedule(run, ms);
  }

  private clearTimer(): void {
    if (this.timer === null) return;
    const cancel = this.ports.clearTimeout ?? ((handle: ReturnType<typeof setTimeout>) => clearTimeout(handle));
    cancel(this.timer);
    this.timer = null;
  }
}
